import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { differenceInMonths, startOfMonth } from "date-fns";
import moment from "moment";
import "moment/locale/pt-br";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { useProfile } from "../../../../context/ProfileContext";
import { ModalComparation, ToastContent, ToolbarEditProposal, dataComparation } from "../../../../shared/components";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { IVFormErros, useVForm } from "../../../../shared/forms";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../../shared/layouts";
import { clientService } from "../../../../shared/services/api/client/clientService";
import { PeopleService } from "../../../../shared/services/api/people/PeopleService";
import {
	IEditProposalGuaranteeData,
	ISimulateEditCancel,
	ISimulateEditLocator,
	ISimulateEditProposal,
	ISimulateEditProposalAddress,
	ISimulateEditProposalValues,
	ProposalEditService,
	TProposalSimulation,
} from "../../../../shared/services/api/proposal/ProposalEditService";
import { IProposalList, ProposalService } from "../../../../shared/services/api/proposal/ProposalService";
import { globalRemoveMask } from "../../../../shared/utils";
import { formatDateForInput } from "../../../../shared/utils/format-date";
import { returnMessageError } from "../../../../shared/utils/returnMessageError";
import { globalCheckCEP } from "../../../../shared/utils/searchCEP";

import { ProposalBasicCardData } from "./ProposalBasicCardData";

import { AddressTab } from "./tabs/AddressTab";
import { CancelTab } from "./tabs/CancelTab";
import { GuaranteeDataTab } from "./tabs/GuaranteeDataTab";
import { LocatorTab } from "./tabs/LocatorTab";
import { ProposalEditTab, ProposalEditTabItem, ProposalEditTabPanel, ProposalEditTabs } from "./tabs/ProposalEditTabs";
import { ValidityTab } from "./tabs/ValidityTab";
import { ValuesTab } from "./tabs/ValuesTab";
import { IClientPlan } from "./types";

import {
	formValidationSchema,
	formValidationSchemaAddress,
	formValidationSchemaCancel,
	formValidationSchemaGuaranteeData,
	formValidationSchemaLocator,
	formValidationSchemaValue,
} from "./proposalEditSchemas";
import { globalTranslate, globalTranslateFinally, globalTranslateMaritalStatus, globalTranslateProfession } from "../../../../shared/utils";

export const ProposalEditPresentation: React.FC = () => {
	const { id = "novo" } = useParams<"id">();
	const navigate = useNavigate();
	const { formRef } = useVForm();
	const [isLoading, setIsLoading] = useState(false);
	const [plano, setPlano] = useState("");
	const { isSuperAdmin } = useProfile();
	const canChangePartnerOrigin =
		isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.ChangePartnerOrigin);

	const [activeTab, setActiveTab] = useState<ProposalEditTab | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	const [contractBegin, setContractBegin] = useState("");
	const [contractEnd, setContractEnd] = useState("");
	const [termBegin, setTermBegin] = useState("");
	const [termEnd, setTermEnd] = useState("");

	const [VLmi, setVLmi] = useState("");
	const [VTaxa, setVTaxa] = useState("");
	const [VRentAmount, setVRentAmount] = useState("");
	const [VLmiAmount, setVLmiAmount] = useState("");
	//const [VInstallment, setVInstallment] = useState("");

	const [listPlans, setListPlans] = useState<IClientPlan[]>([]);
	const [openModalSimulation, setOpenModalSimulation] = useState(false);
	const [LoagindData, setLoadData] = useState<dataComparation[]>([]);
	let meusdados: dataComparation[] = [];
	const [rows, setRows] = useState<IProposalList>();
	const [dataValidity, setDataValidity] = useState<ISimulateEditProposal>();
	const [dataValues, setDataValues] = useState<ISimulateEditProposalValues>();
	const [dataAddress, setDataAddess] = useState<ISimulateEditProposalAddress>();
	const [dataGuaranteeData, setDataGuaranteeData] = useState<IEditProposalGuaranteeData>();
	const [partnerOptions, setPartnerOptions] = useState<{ id: string; label: string }[]>([]);
	const [dataLocator, setDataLocator] = useState<ISimulateEditLocator>();
	const [dataCancel, setDataCancel] = useState<ISimulateEditCancel>();
	const [sinisters, setSinisters] = useState<any[] | undefined>([]);
	const [hasSinister, setHasSinister] = useState(false);
	const [status, setStatus] = useState<string>("");
	const [document, setDocument] = useState<string>("");
	const toastHandler = useToast();
	const { debounce } = useDebounce();
	const [modified, setModified] = useState(false);
	const [editAddress, setEditAddress] = useState(false);
	const [documentType, setDocumentType] = useState<string | undefined>("");

	const availableTabs = useMemo<ProposalEditTabItem[]>(() => {
		const tabs: ProposalEditTabItem[] = [];

		if (isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.UpProposalPeriod)) {
			tabs.push({ key: "validity", label: "Vigência" });
		}

		if (isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.UpProposalValues)) {
			tabs.push({ key: "values", label: "Valores" });
		}

		if (isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.UpProposalAddress)) {
			tabs.push({ key: "address", label: "Endereço" });
		}

		if (isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.Update)) {
			tabs.push({ key: "guaranteeData", label: "Dados da garantia" });
		}

		if (status === "active" && (isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.UpProposalLocator)) && !hasSinister) {
			tabs.push({ key: "locator", label: "Locador" });
		}

		if (status === "canceled" && (isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.UpProposalCancel))) {
			tabs.push({ key: "cancel", label: "Cancelamento" });
		}

		return tabs;
	}, [status, isSuperAdmin, hasSinister]);

	const handleGuaranteeDataChange = (dadosRecebidos: IEditProposalGuaranteeData) => {
		const dados: IEditProposalGuaranteeData = { ...dadosRecebidos };

		// Quando a origem é "Aplicação", não há parceiro selecionável — usamos o parceiro Garantti como padrão.
		if (dados.createdFrom === "web_app") {
			const garanttiPartner = partnerOptions.find((partner) => partner.label?.toLowerCase().includes("garantti"));
			if (garanttiPartner) dados.partnerId = garanttiPartner.id;
		}

		setIsLoading(true);

		formValidationSchemaGuaranteeData
			.validate(dados, { abortEarly: false })
			.then((dadosValidados) => {
				setIsLoading(true);
				ProposalEditService.simulateGuaranteeData(id, dadosValidados)
					.then((result) => {
						if (!result || result instanceof Error || !result.proposal || !result.newProposal) {
							presentSimulationError(result?.message);
							return;
						}
						setIsLoading(false);
						setDataGuaranteeData(dadosValidados);
						meusdados.push({
							Field: "Origem",
							ValueBack: globalTranslate(result.proposal.createdFrom || rows?.createdFrom || ""),
							ValueNew: globalTranslate(result.newProposal.createdFrom || dadosValidados.createdFrom || ""),
						});
						meusdados.push({
							Field: "Vínculo Empregatício",
							ValueBack: globalTranslateProfession(result.proposal.employmentRelationShip || rows?.employmentRelationShip || ""),
							ValueNew: globalTranslateProfession(result.newProposal.employmentRelationShip || dadosValidados.employmentRelationShip || ""),
						});
						meusdados.push({
							Field: "Estado Civil",
							ValueBack: globalTranslateMaritalStatus(result.proposal.maritalStatus || rows?.maritalStatus || ""),
							ValueNew: globalTranslateMaritalStatus(result.newProposal.maritalStatus || dadosValidados.maritalStatus || ""),
						});
						meusdados.push({
							Field: "Parceiro",
							ValueBack: getPartnerLabel(result.proposal.partnerId || rows?.partnerId || ""),
							ValueNew: getPartnerLabel(result.newProposal.partnerId || dadosValidados.partnerId || ""),
						});
						setLoadData(meusdados);
						handleShow();
					})
					.catch((error) => {
						presentSimulationError(error?.message);
					});
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (activeTab !== "guaranteeData") return;
		if (partnerOptions.length) return;

		clientService
			.getTypePartners()
			.then((result) => {
				if (result instanceof Error) {
					setPartnerOptions([]);
					return;
				}

				setPartnerOptions(
					result.data.map((partner) => ({
						id: partner.id,
						label: partner.trade_name || partner.name,
					})),
				);
			})
			.catch(() => {
				setPartnerOptions([]);
			});
	}, [activeTab, partnerOptions.length]);

	useEffect(() => {
		if (!availableTabs.length) {
			setActiveTab(null);
			return;
		}

		if (!activeTab || !availableTabs.some((t) => t.key === activeTab)) {
			setActiveTab(availableTabs[0].key);
		}
	}, [activeTab, availableTabs]);

	const toNumberOrZero = (value: string) => {
		const parsed = parseFloat(globalRemoveMask(value ?? ""));
		return Number.isFinite(parsed) ? parsed : 0;
	};

	const normalizePlanId = (value: unknown) => (value ?? "") as string;

	const getPartnerLabel = (partnerId?: string) => {
		if (!partnerId) return "Não informado";

		return partnerOptions.find((partner) => partner.id === partnerId)?.label || partnerId;
	};

	const applyPlanValuesFromClient = (planId: string, rentAmount: string) => {
		const selected = listPlans?.find((p) => p?.plan?.id === planId || p?.planId === planId || p?.planoId === planId);
		const lmi = selected?.lmi ?? selected?.plan?.lmi ?? "";
		const fee = selected?.residentialFee ?? selected?.plan?.residentialFee ?? "";

		setVLmi(lmi);
		setVTaxa(fee);

		const rent = toNumberOrZero(rentAmount);
		const lmiNumber = parseInt(lmi || "0", 10);
		const feeNumber = parseFloat(fee || "0");

		const lmiAmount = (rent * lmiNumber).toFixed(2);
		const assuranceMonthAmount = (rent * (feeNumber / 100)).toFixed(2);

		setVLmiAmount(lmiAmount);

		formRef.current?.setData({
			rentAmount,
			planId,
			lmi: lmiNumber ? String(lmiNumber) : "",
			fee: Number.isFinite(feeNumber) ? String(feeNumber) : "",
			lmiAmount,
			assuranceMonthAmount,
		});
	};

	const handlePlanChange = (rawPlanId: unknown) => {
		const newPlanId = normalizePlanId(rawPlanId);
		setPlano(newPlanId);
		formRef.current?.setFieldValue("planId", newPlanId);

		if (newPlanId === "custom") {
			setModified(true);
			return;
		}

		setModified(false);
		applyPlanValuesFromClient(newPlanId, VRentAmount);
	};

	const handleShow = () => {
		setOpenModalSimulation(true);
	};

	const presentSimulationError = (message?: string) => {
		setIsLoading(false);
		toastHandler.present({
			type: "error",
			position: "top-right",
			messageOrContent: <ToastContent title="Alerta!!" content={"Erro: " + (message || "A simulação não retornou a comparação da proposta.")} />,
		});
	};

	const handleSimulationValues = (dados: ISimulateEditProposalValues) => {
		setIsLoading(true);
		dados.rentAmount = globalRemoveMask(dados.rentAmount);
		dados.lmiAmount = globalRemoveMask(dados.lmiAmount);
		dados.assuranceMonthAmount = globalRemoveMask(dados.assuranceMonthAmount);
		formValidationSchemaValue
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados) => {
				setIsLoading(true);
				ProposalEditService.simulateValue(id, dadosvalidados)
					.then((result) => {
						if (!result || result instanceof Error || !result.proposal || !result.newProposal) {
							presentSimulationError(result?.message);
							return;
						}
						setDataValues(dados);
						meusdados.push({ Field: "Plano", ValueBack: result.proposal.planId, ValueNew: result.newProposal.planId as string });
						meusdados.push({ Field: "LMI", ValueBack: result.proposal.lmi, ValueNew: result.newProposal.lmi as string });
						meusdados.push({
							Field: "Valor Lmi",
							ValueBack: parseFloat(`${result.proposal.lmiAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
							ValueNew: parseFloat(`${result.newProposal.lmiAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
						});
						meusdados.push({
							Field: "Valor Aluguel",
							ValueBack: parseFloat(`${result.proposal.rentAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
							ValueNew: parseFloat(`${result.newProposal.rentAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
						});
						meusdados.push({
							Field: "Taxa Aplicada",
							ValueBack: result.proposal.residentialFee,
							ValueNew: result.newProposal.residentialFee as string,
						});
						meusdados.push({
							Field: "Valor Mensalidade",
							ValueBack: parseFloat(`${result.proposal.assuranceMonthAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
							ValueNew: parseFloat(`${result.newProposal.assuranceMonthAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
						});
						setLoadData(meusdados);
						setIsLoading(false);
						handleShow();
					})
					.catch((error) => {
						presentSimulationError(error?.message);
					});
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				console.log(validationErros);
				formRef.current?.setErrors(validationErros);
				setIsLoading(false);
			});
	};

	const handleSimulation = (dados: ISimulateEditProposal) => {
		setIsLoading(true);
		delete dados.rentMonthsCount;
		delete dados.rentMonthsCountContract;
		formValidationSchema
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados) => {
				setIsLoading(true);
				ProposalEditService.simulate(id, dadosvalidados)
					.then((result) => {
						if (!result || result instanceof Error || !result.proposal || !result.newProposal) {
							presentSimulationError(result?.message);
							return;
						}
						setDataValidity(dados);
						meusdados.push({
							Field: "Inicio Contrato Locação",
							ValueBack: moment(`${result.proposal.contractBegin.substring(0, 10)}T03:00:00.000Z`).format("L"),
							ValueNew: moment(`${result.newProposal.contractBegin.substring(0, 10)}T03:00:00.000Z`).format("L"),
						});
						meusdados.push({
							Field: "Fim do Contrato Locação",
							ValueBack: moment(`${result.proposal.contractEnd.substring(0, 10)}T03:00:00.000Z`).format("L"),
							ValueNew: moment(`${result.newProposal.contractEnd.substring(0, 10)}T03:00:00.000Z`).format("L"),
						});
						meusdados.push({
							Field: "Inicio da Vigência",
							ValueBack: moment(`${result.proposal.termBegin.substring(0, 10)}T03:00:00.000Z`).format("L"),
							ValueNew: moment(`${result.newProposal.termBegin.substring(0, 10)}T03:00:00.000Z`).format("L"),
						});
						meusdados.push({
							Field: "Fim da Vigência",
							ValueBack: moment(`${result.proposal.expiresAt.substring(0, 10)}T03:00:00.000Z`).format("L"),
							ValueNew: moment(`${result.newProposal.expiresAt.substring(0, 10)}T03:00:00.000Z`).format("L"),
						});
						meusdados.push({
							Field: "Meses de Vigência",
							ValueBack: result.proposal.rentMonthsCount,
							ValueNew: result.newProposal.rentMonthsCount as string,
						});
						meusdados.push({
							Field: "Valor da Parcela",
							ValueBack: parseFloat(`${result.proposal.assuranceMonthAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
							ValueNew: parseFloat(`${result.proposal.assuranceMonthAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" }),
						});
						meusdados.push({
							Field: "Quantidade de parcelas",
							ValueBack: result.proposal.rentMonthsCount,
							ValueNew: result.newProposal.rentMonthsCount as string,
						});
						setLoadData(meusdados);
						setIsLoading(false);
						handleShow();
					})
					.catch((error) => {
						presentSimulationError(error?.message);
					});
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				console.log(validationErros);
				formRef.current?.setErrors(validationErros);
				setIsLoading(false);
			});
	};

	const handleSimulationAddress = (dados: ISimulateEditProposalAddress) => {
		setIsLoading(true);
		formValidationSchemaAddress
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados) => {
				setIsLoading(true);
				ProposalEditService.simulateAddress(id, dadosvalidados)
					.then((result) => {
						if (!result || result instanceof Error || !result.proposal || !result.newProposal) {
							presentSimulationError(result?.message);
							return;
						}
						setDataAddess(dados);
						meusdados.push({ Field: "Endereço", ValueBack: result.proposal.addressStreet, ValueNew: result.newProposal.addressStreet });
						meusdados.push({ Field: "Numero", ValueBack: result.proposal.addressNumber, ValueNew: result.newProposal.addressNumber });
						meusdados.push({ Field: "Complemento", ValueBack: result.proposal.addressComplement, ValueNew: result.newProposal.addressComplement });
						meusdados.push({ Field: "Bairro", ValueBack: result.proposal.addressDistrict, ValueNew: result.newProposal.addressDistrict });
						meusdados.push({ Field: "Cidade", ValueBack: result.proposal.addressCity, ValueNew: result.newProposal.addressCity });
						meusdados.push({ Field: "Estado", ValueBack: result.proposal.addressState, ValueNew: result.newProposal.addressState });
						meusdados.push({ Field: "Cep", ValueBack: result.proposal.addressZipcode, ValueNew: result.newProposal.addressZipcode as string });
						meusdados.push({ Field: "Cód IBGE", ValueBack: result.proposal.addressIBGE ?? "", ValueNew: result.newProposal.addressIBGE ?? "" });
						meusdados.push({
							Field: "Tipo de locação",
							ValueBack: globalTranslateFinally(result.proposal.propertyFinally ?? rows?.propertyFinally ?? "").toString(),
							ValueNew: globalTranslateFinally(result.newProposal.propertyFinally ?? dados.propertyFinally ?? "").toString(),
						});
						if (result.proposal.fee !== undefined || result.newProposal.fee !== undefined) {
							meusdados.push({ Field: "Taxa (mantida)", ValueBack: String(result.proposal.fee ?? ""), ValueNew: String(result.newProposal.fee ?? "") });
						}
						setLoadData(meusdados);
						setIsLoading(false);
						handleShow();
					})
					.catch((error) => {
						presentSimulationError(error?.message);
					});
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				console.log(validationErros);
				formRef.current?.setErrors(validationErros);
				setIsLoading(false);
			});
	};

	const handleLocatorChange = (dados: ISimulateEditLocator) => {
		setIsLoading(true);

		formValidationSchemaLocator
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados: any) => {
				setIsLoading(true);

				ProposalEditService.simulateLocator(id, dadosvalidados)
					.then((result: TProposalSimulation<any>) => {
						setIsLoading(false);
						if (result instanceof Error) {
							const titleMessage = "Alerta!!";
							const contentMessage = "Erro: " + result?.message;

							toastHandler.present({
								type: "error",
								position: "top-right",
								messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
							});
						} else {
							setDataLocator(dados);
							meusdados.push({ Field: "Documento", ValueBack: result.proposal.lessorCpf, ValueNew: result.newProposal.lessorCpf });
							meusdados.push({ Field: "Nome", ValueBack: result.proposal.lessorName, ValueNew: result.newProposal.lessorName });
							meusdados.push({
								Field: "Data de nascimento",
								ValueBack: result.proposal.lessorBirthDate
									? moment(`${String(result.proposal.lessorBirthDate).substring(0, 10)}T03:00:00.000Z`).format("L")
									: "",
								ValueNew: result.newProposal.lessorBirthDate
									? moment(`${String(result.newProposal.lessorBirthDate).substring(0, 10)}T03:00:00.000Z`).format("L")
									: "",
							});
							meusdados.push({ Field: "Celular", ValueBack: result.proposal.lessorMobilePhone, ValueNew: result.newProposal.lessorMobilePhone });
							meusdados.push({ Field: "Telefone", ValueBack: result.proposal.lessorPhone, ValueNew: result.newProposal.lessorPhone });
							meusdados.push({ Field: "E-mail", ValueBack: result.proposal.lessorEmail, ValueNew: result.newProposal.lessorEmail });
							meusdados.push({
								Field: "Tipo de Locador",
								ValueBack: result.proposal.lessorTypePerson,
								ValueNew: result.newProposal.lessorTypePerson,
							});
							meusdados.push({ Field: "Motivo", ValueBack: result.proposal.obs, ValueNew: result.newProposal.obs });
							setLoadData(meusdados);
							handleShow();
						}
					})
					.catch((err: any) => {
						setIsLoading(false);

						const { titleMessage, contentMessage } = returnMessageError(err);

						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					});
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				console.log(validationErros);
				formRef.current?.setErrors(validationErros);
				setIsLoading(false);
			});
	};

	const handleCancelChange = (dados: ISimulateEditCancel) => {
		setIsLoading(true);
		dados.canceledAt = dados.canceledAt != null ? dados.canceledAt : "";
		dados.canceledReason = dados.canceledReason != null ? dados.canceledReason : "";
		dados.canceledType = dados.canceledType != null ? dados.canceledType : "";

		formValidationSchemaCancel
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados: any) => {
				setIsLoading(false);

				setDataCancel(dados);
				meusdados.push({ Field: "Motivo do cancelamento", ValueBack: rows?.canceledType, ValueNew: dados.canceledType });
				meusdados.push({
					Field: "Data do cancelamento",
					ValueBack: moment(`${rows?.canceledAt?.substring(0, 10)}T03:00:00.000Z`).format("L"),
					ValueNew: moment(`${dados?.canceledAt?.substring(0, 10)}T03:00:00.000Z`).format("L"),
				});
				meusdados.push({ Field: "Descrição do cancelamento", ValueBack: rows?.canceledReason, ValueNew: dados.canceledReason });
				setLoadData(meusdados);
				handleShow();
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				console.log(validationErros);
				formRef.current?.setErrors(validationErros);
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (sinisters?.length && status == "active" && isSuperAdmin) {
			const length = sinisters.length;
			const canceledSinisters = sinisters.filter((sinister) => sinister?.status === "canceled");
			if (canceledSinisters?.length < length) {
				setHasSinister(true);
			}
		}
	}, [sinisters]);

	useEffect(() => {
		setIsLoading(true);
		ProposalService.getById(`${id}`).then((result) => {
			setIsLoading(false);
			if (result instanceof Error) {
				alert(result.message);
				navigate(`/proposal/detail/${id}`);
			} else {
				setRows(result);
				setSinisters(result.sinister);
				setStatus(result.status);
				setDocument(result.document);
				setTermBegin(result.termBegin!);
				setTermEnd(result.expiresAt!);
				setContractBegin(result.contractBegin!);
				setContractEnd(result.contractEnd!);
				//console.log('249....',result.lmi)
				if (result.lmi == "0") {
					setVLmi("12");
				} else {
					setVLmi(result.lmi!);
				}
				setVTaxa(result.residentialFee!);
				setVRentAmount(result.rentAmount!);
				setVLmiAmount(result.lmiAmount!);
				//setVInstallment(result.assuranceMonthAmount!);
				setPlano(result.planId!);
			}
		});
	}, [id, refreshKey]);

	const handleCPFOrCNPJ = (value: string) => {
		const documentCPFOrCNPJ = value?.replace(/[^0-9]/g, "");
		const documentLocator = document?.replace(/[^0-9]/g, "");

		if (documentCPFOrCNPJ === documentLocator) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Erro "} content={"Documento do locador deve ser diferente do documento do locatário"} />,
			});

			formRef.current?.setFieldValue("lessorName", "");
			formRef.current?.setFieldValue("lessorBirthDate", "");
			formRef.current?.setFieldValue("lessorPhone", "");
			formRef.current?.setFieldValue("lessorMobilePhone", "");
			formRef.current?.setFieldValue("lessorEmail", "");
		} else {
			PeopleService.getPersonDataByDocument(documentCPFOrCNPJ)
				.then((data) => {
					formRef.current?.setFieldValue("lessorName", data.naturalPersonData?.name);
					formRef.current?.setFieldValue("lessorBirthDate", data.naturalPersonData?.birthDate);
					formRef.current?.setFieldValue("lessorPhone", data.naturalPersonData?.phone);
					formRef.current?.setFieldValue("lessorMobilePhone", data.naturalPersonData?.mobilePhone);
					formRef.current?.setFieldValue("lessorEmail", data.naturalPersonData?.email);
				})
				.catch((err) => {
					const messageTitle = "Erro na pesquisa";
					const messageContent = "Documento não localizado na base, verifique se esta correto.";

					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={messageTitle} content={messageContent} />,
					});
				});
		}
	};

	useEffect(() => {
		if (activeTab !== "address") {
			setEditAddress(false);
		}

		if (!activeTab) return;
		if (!rows) return;

		if (activeTab === "validity") {
			formRef.current?.setData({
				termBegin: formatDateForInput(rows?.termBegin ? new Date(`${rows?.termBegin.substring(0, 10)}T03:00:00.000Z`) : new Date("2023-01-1899")),
				contractBegin: formatDateForInput(
					rows?.contractBegin ? new Date(`${rows?.contractBegin.substring(0, 10)}T03:00:00.000Z`) : new Date("2023-01-1899"),
				),
				contractEnd: formatDateForInput(rows?.contractEnd ? new Date(`${rows?.contractEnd.substring(0, 10)}T03:00:00.000Z`) : new Date("2023-01-1899")),
				expiresAt: formatDateForInput(rows?.expiresAt ? new Date(`${rows?.expiresAt.substring(0, 10)}T03:00:00.000Z`) : new Date("2023-01-1899")),
				rentMonthsCount: rows?.rentMonthsCount ? rows?.rentMonthsCount : 1,
				rentMonthsCountContract: rows?.rentMonthsCount ? rows?.rentMonthsCount : 1,
			});
			return;
		}

		if (activeTab === "values") {
			formRef.current?.setData({
				rentAmount: rows?.rentAmount,
				fee: rows?.residentialFee,
				assuranceMonthAmount: rows?.assuranceMonthAmount,
				lmi: rows?.lmi,
				lmiAmount: rows?.lmiAmount,
				planId: rows?.planId,
			});

			setVRentAmount(rows?.rentAmount || "");
			setVTaxa(rows?.residentialFee || "");
			setVLmi(rows?.lmi || "");
			setVLmiAmount(rows?.lmiAmount || "");
			setPlano(rows?.planId || "");
			setModified((rows?.planId || "") === "custom");
			return;
		}

		if (activeTab === "address") {
			formRef.current?.setData({
				addressStreet: rows?.addressStreet,
				addressNumber: rows?.addressNumber,
				addressComplement: rows?.addressComplement,
				addressDistrict: rows?.addressDistrict,
				addressCity: rows?.addressCity,
				addressState: rows?.addressState,
				addressZipcode: rows?.addressZipcode,
				addressIBGE: rows?.addressIBGE ?? rows?.addressIbge,
				propertyFinally: rows?.propertyFinally,
			});

			if (isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.UpProposalAddress)) {
				setEditAddress(true);
			}
			return;
		}

		if (activeTab === "guaranteeData") {
			formRef.current?.setData({
				createdFrom: rows?.createdFrom,
				employmentRelationShip: rows?.employmentRelationShip,
				maritalStatus: rows?.maritalStatus,
				partnerId: rows?.partnerId,
			});
			return;
		}

		if (activeTab === "locator") {
			formRef.current?.setData({
				lessorName: rows?.lessorName,
				lessorCpf: rows?.lessorCpf,
				lessorBirthDate: rows?.lessorBirthDate,
				lessorEmail: rows?.lessorEmail,
				lessorPhone: rows?.lessorPhone,
				lessorMobilePhone: rows?.lessorMobilePhone,
				lessorTypePerson: rows?.lessorTypePerson,
				obs: rows?.obs,
			});
			setDocumentType(rows?.lessorTypePerson);
			return;
		}

		if (activeTab === "cancel") {
			formRef.current?.setData({
				canceledReason: rows?.canceledReason,
				canceledType: rows?.canceledType,
				canceledAt: rows?.canceledAt,
			});
		}
	}, [activeTab, rows]);

	useEffect(() => {
		if (activeTab !== "values") return;
		if (!rows?.clientId) return;

		setIsLoading(true);
		clientService
			.getByID(rows.clientId)
			.then((result) => {
				if (result instanceof Error) {
					alert(result.message);
					return;
				}
				const clientPlan = result.__client_plan__ as unknown as IClientPlan[];
				setListPlans(clientPlan || []);
			})
			.finally(() => setIsLoading(false));
	}, [activeTab, rows?.clientId]);

	useEffect(() => {
		if (activeTab !== "values") return;
		if (!plano || plano === "custom") return;
		if (!listPlans?.length) return;

		applyPlanValuesFromClient(plano, VRentAmount);
	}, [activeTab, listPlans]);

	useEffect(() => {
		if (activeTab !== "values") return;
		if (VLmi && VRentAmount && VTaxa) {
			debounce(() => {
				if (plano == "custom") {
					setModified(true);
				} else {
					setModified(false);
				}

				const rent = toNumberOrZero(VRentAmount);
				const lmiNumber = parseInt(VLmi || "0", 10);
				const feeNumber = parseFloat(VTaxa || "0");

				formRef.current?.setData({
					lmiAmount: (rent * lmiNumber).toFixed(2),
					rentAmount: VRentAmount,
					assuranceMonthAmount: (rent * (feeNumber / 100)).toFixed(2),
					lmi: lmiNumber ? String(lmiNumber) : "",
					fee: Number.isFinite(feeNumber) ? String(feeNumber) : "",
					planId: plano,
				});
			});
		}
	}, [activeTab, VLmi, VRentAmount, VTaxa, plano]);

	useEffect(() => {
		if (activeTab !== "validity") return;
		if (termBegin && termEnd && contractBegin && contractEnd) {
			debounce(() => {
				const difMonthsValidity = differenceInMonths(startOfMonth(new Date(termEnd)), startOfMonth(new Date(termBegin)));
				const difMonthsContract = differenceInMonths(startOfMonth(new Date(contractEnd)), startOfMonth(new Date(contractBegin)));
				formRef.current?.setData({
					rentMonthsCountContract: difMonthsContract,
					rentMonthsCount: difMonthsValidity,
					termBegin: formatDateForInput(new Date(`${termBegin.substring(0, 10)}T03:00:00.000Z`)),
					expiresAt: formatDateForInput(new Date(`${termEnd.substring(0, 10)}T03:00:00.000Z`)),
					contractBegin: formatDateForInput(new Date(`${contractBegin.substring(0, 10)}T03:00:00.000Z`)),
					contractEnd: formatDateForInput(new Date(`${contractEnd.substring(0, 10)}T03:00:00.000Z`)),
				});
			});
		}
	}, [activeTab, contractBegin, contractEnd, termBegin, termEnd]);

	const handleSaveUpdate = () => {
		if (!activeTab) return;

		if (activeTab === "validity" && dataValidity) {
			ProposalEditService.updateValidity(id, dataValidity)
				.then((result: any) => {
					setIsLoading(false);
					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						setOpenModalSimulation(false);
						setRefreshKey((prev) => prev + 1);
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={"Sucesso"} content={"Garantia Atualizada com sucesso"} />,
						});
					}
				})
				.catch((error) => {
					setIsLoading(false);
					const titleMessage = "Alerta!!";
					const contentMessage = "Erro: " + error?.message;
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
			return;
		}

		if (activeTab === "values" && dataValues) {
			ProposalEditService.updateValues(id, dataValues)
				.then((result: any) => {
					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;
						setOpenModalSimulation(false);
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						setRefreshKey((prev) => prev + 1);
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={"Sucesso"} content={"Garantia Atualizada com sucesso"} />,
						});
					}
				})
				.catch((error) => {
					setIsLoading(false);
					const titleMessage = "Alerta!!";
					const contentMessage = "Erro: " + error?.message;
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
			return;
		}

		if (activeTab === "address" && dataAddress) {
			ProposalEditService.updateAddress(id, dataAddress)
				.then((result: any) => {
					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;
						setOpenModalSimulation(false);
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						setRefreshKey((prev) => prev + 1);
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={"Sucesso"} content={"Garantia Atualizada com sucesso"} />,
						});
					}
				})
				.catch((error) => {
					setIsLoading(false);
					const titleMessage = "Alerta!!";
					const contentMessage = "Erro: " + error?.message;
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
			return;
		}

		if (activeTab === "guaranteeData" && dataGuaranteeData) {
			setIsLoading(true);
			ProposalEditService.updateGuaranteeData(id, dataGuaranteeData)
				.then((result) => {
					setIsLoading(false);
					setOpenModalSimulation(false);
					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						setRefreshKey((prev) => prev + 1);
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={"Sucesso"} content={"Garantia atualizada com sucesso"} />,
						});
					}
				})
				.catch((error: any) => {
					setIsLoading(false);
					setOpenModalSimulation(false);
					const titleMessage = "Alerta!!";
					const contentMessage = "Erro: " + error?.message;
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
			return;
		}

		if (activeTab === "locator" && dataLocator) {
			setIsLoading(true);
			ProposalEditService.updateLocator(id, dataLocator)
				.then((result: TProposalSimulation<any>) => {
					setIsLoading(false);
					setOpenModalSimulation(false);
					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;

						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						setRefreshKey((prev) => prev + 1);
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={"Sucesso"} content={"Garantia Atualizada com sucesso"} />,
						});
					}
				})
				.catch((error: any) => {
					setIsLoading(false);
					setOpenModalSimulation(false);
					const titleMessage = "Alerta!!";
					const contentMessage = "Erro: " + error?.message;
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
			return;
		}

		if (activeTab === "cancel" && dataCancel) {
			setIsLoading(true);
			ProposalEditService.updateCancel(id, dataCancel)
				.then((result: TProposalSimulation<any>) => {
					setIsLoading(false);
					setOpenModalSimulation(false);
					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;

						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						setRefreshKey((prev) => prev + 1);
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={"Sucesso"} content={"Garantia Atualizada com sucesso"} />,
						});
					}
				})
				.catch((error: any) => {
					setIsLoading(false);
					setOpenModalSimulation(false);
					const titleMessage = "Alerta!!";
					const contentMessage = "Erro: " + error?.message;
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		}
	};

	const checkCEP = async (e: any) => {
		try {
			await globalCheckCEP(formRef, e);
		} catch {
			// evita quebrar o fluxo do form em caso de falha do ViaCEP
		}
	};

	return (
		<LayoutBaseDePagina
			titulo="Garantias"
			subtitulo={`Alteração de Garantia`}
			barraDeFerramentas={
				<ToolbarEditProposal
					clickBack={() => navigate(`/garantias/visualizar/${id}`)}
					clickSave={() => {
						if (!activeTab) return;
						formRef.current?.submitForm();
					}}
					isDisabledConfirm={!activeTab}
					showButtonSave={availableTabs.length > 0}
					showButtonEditValidity={false}
					showButtonEditValues={false}
					showButtonEditAddress={false}
					showButtonEditLocator={false}
					showButtonEditCancel={false}
					clickEditValues={() => {}}
					clickValidity={() => {}}
					clickEditAddress={() => {}}
					clickEditLocator={() => {}}
					clickEditCancel={() => {}}
				/>
			}
		>
			{openModalSimulation && (
				<ModalComparation
					ITitle="Alteração de garantia"
					TitleBack="Contrato Original"
					TitleNew="Contrato Alterado"
					isOpenModal={openModalSimulation}
					dataComparation={LoagindData}
					showButtonConfirm={!isLoading}
					textButtonConfirm="Confirmar"
					textButtonCancel="Desistir"
					showButtonCancel={true}
					isCloseModal={() => {
						setOpenModalSimulation(false);
					}}
					onclickConfirm={() => {
						(handleSaveUpdate(), setOpenModalSimulation(false));
					}}
				/>
			)}

			<Box sx={{ width: "100%", maxWidth: "1300px" }}>
				<Grid container gap={1} sx={{ width: "100%" }}>
					{rows && <ProposalBasicCardData row={rows} />}
				</Grid>

				{activeTab && availableTabs.length > 0 && (
					<Card sx={{ width: "100%", mt: 1, ml: 1, borderRadius: 4 }}>
						<CardContent>
							<Typography variant="h6" color="primary" sx={{ mb: 1 }}>
								Alterações disponíveis
							</Typography>
							<ProposalEditTabs value={activeTab} tabs={availableTabs} onChange={setActiveTab} />
							<Box
								sx={{
									mt: 0,
									maxHeight: { xs: "none", md: "calc(100vh - 420px)" },
									overflowY: { xs: "visible", md: "auto" },
									overflowX: "hidden",
								}}
							>
								<ProposalEditTabPanel value={activeTab} tab="validity">
									<ValidityTab
										formRef={formRef}
										isLoading={isLoading}
										onSubmit={handleSimulation}
										setContractBegin={setContractBegin}
										setContractEnd={setContractEnd}
										setTermBegin={setTermBegin}
										setTermEnd={setTermEnd}
									/>
								</ProposalEditTabPanel>

								<ProposalEditTabPanel value={activeTab} tab="values">
									<ValuesTab
										formRef={formRef}
										isLoading={isLoading}
										listPlans={listPlans}
										modified={modified}
										onSubmit={handleSimulationValues}
										onPlanChange={handlePlanChange}
										setVLmi={setVLmi}
										setVLmiAmount={setVLmiAmount}
										setVRentAmount={setVRentAmount}
										setVTaxa={setVTaxa}
									/>
								</ProposalEditTabPanel>

								<ProposalEditTabPanel value={activeTab} tab="address">
									<AddressTab
										formRef={formRef}
										isLoading={isLoading}
										editAddress={editAddress}
										onSubmit={handleSimulationAddress}
										onCheckCEP={checkCEP}
									/>
								</ProposalEditTabPanel>

									<ProposalEditTabPanel value={activeTab} tab="guaranteeData">
										<GuaranteeDataTab
										formRef={formRef}
										isLoading={isLoading}
										onSubmit={handleGuaranteeDataChange}
										createdFrom={rows?.createdFrom}
										canChangePartnerOrigin={canChangePartnerOrigin}
									/>
									</ProposalEditTabPanel>

								<ProposalEditTabPanel value={activeTab} tab="locator">
									<LocatorTab
										formRef={formRef}
										isLoading={isLoading}
										documentType={documentType}
										setDocumentType={setDocumentType}
										rows={rows}
										onSubmit={handleLocatorChange}
										onBlurDocument={handleCPFOrCNPJ}
									/>
								</ProposalEditTabPanel>

								<ProposalEditTabPanel value={activeTab} tab="cancel">
									<CancelTab formRef={formRef} isLoading={isLoading} onSubmit={handleCancelChange} />
								</ProposalEditTabPanel>
							</Box>
						</CardContent>
					</Card>
				)}
			</Box>
		</LayoutBaseDePagina>
	);
};
